import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { PageHeader, Spinner, cn, fieldControlClass } from '@helios/ui';
import {
  PageSectionWorkspace,
  type PageWorkspaceSection,
} from '@/components/layout/PageSectionWorkspace';

/** Same box as `@helios/ui` fields: h-7, text-xs, no extra vertical padding. */
export const settingsControlClass = cn(
  fieldControlClass,
  'h-[var(--helios-control-height,28px)] w-full',
);

/** Labeled filters plus a trailing action. Pin the action to the control baseline. */
export const settingsFilterRowClass = 'grid items-end gap-3';

export const settingsFilterActionClass = 'flex items-end';

export function SettingsPageFrame({
  title,
  description,
  eyebrow,
  actions,
  children,
  sections,
  activeSectionId,
  onSectionChange,
}: {
  title: string;
  description?: string;
  eyebrow?: string;
  actions?: ReactNode;
  children?: ReactNode;
  sections?: readonly PageWorkspaceSection[];
  activeSectionId?: string;
  onSectionChange?: (id: string) => void;
}) {
  // Only direct, explicitly named section components are partitioned. A form
  // wrapper remains intact, so submit/validation ownership is never changed.
  const blocks = Children.toArray(children);
  const named = blocks.filter(
    (child): child is ReactElement<{ id?: string; title: string; children: ReactNode }> =>
      isValidElement(child) &&
      child.type === SettingsSection &&
      typeof (child.props as { title?: unknown }).title === 'string',
  );
  const automatic =
    named.length > 1
      ? named.map((child, index) => ({
          id: child.props.id ?? `settings-section-${index}`,
          label: child.props.title,
          content: child,
        }))
      : undefined;
  const workspaceSections = sections ?? automatic;
  const pinned = blocks.filter((child) => !named.includes(child as (typeof named)[number]));
  if (workspaceSections?.length) {
    return (
      <div data-settings-page-frame="" className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <PageSectionWorkspace
          ariaLabel={`${title} sections`}
          layout={sections ? 'tabs' : 'auto'}
          sections={workspaceSections}
          activeSectionId={activeSectionId}
          onSectionChange={onSectionChange}
          header={
            <PageHeader
              className="pb-2"
              eyebrow={eyebrow}
              title={title}
              description={description}
              actions={actions}
            />
          }
          toolbar={sections ? children : pinned.length ? pinned : undefined}
        />
      </div>
    );
  }
  return (
    <div data-settings-page-frame="" className="flex min-w-0 shrink-0 flex-col gap-2.5">
      <PageHeader
        className="pb-2"
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={actions}
      />
      {children}
    </div>
  );
}

export function SettingsSection({
  id,
  title,
  description,
  actions,
  children,
  className,
}: {
  id?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  // rounded-lg = --radius (8px). rounded-md maps to the 9999px control pill and
  // clips a full-width section into a giant oval over the main pane.
  return (
    <section
      id={id}
      className={cn(
        'border-border bg-card min-w-0 shrink-0 overflow-hidden rounded-lg border',
        className,
      )}
    >
      <header className="hx-section-header border-border flex flex-wrap items-center justify-between gap-2 border-b px-3 py-1.5">
        <div className="min-w-0">
          <h2 className="hx-section-title text-sm font-semibold">{title}</h2>
          {description ? <p className="text-2xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
        ) : null}
      </header>
      <div className="p-2.5">{children}</div>
    </section>
  );
}

export function SettingsTable({
  columns,
  children,
  minWidth = 640,
  label,
}: {
  columns: string[];
  children: ReactNode;
  minWidth?: number;
  label?: string;
}) {
  return (
    <SettingsTableScroll label={label}>
      <table className="w-full border-collapse text-left text-xs" style={{ minWidth }}>
        <thead className="bg-secondary/40 text-2xs text-muted-foreground uppercase tracking-wide">
          <tr>
            {columns.map((column) => (
              <th key={column} className="px-2 py-1.5 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </SettingsTableScroll>
  );
}

/** Tables scroll horizontally; the Settings body owns their vertical overflow. */
export function SettingsTableScroll({
  children,
  label = 'Settings table',
  className,
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <div
      data-settings-table-scroll=""
      role="region"
      aria-label={label}
      tabIndex={0}
      className={cn(
        'border-border focus-visible:ring-ring min-w-0 max-w-full overflow-x-auto rounded-lg border focus-visible:outline-none focus-visible:ring-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SettingsTableRow({ children }: { children: ReactNode }) {
  return <tr className="border-border/70 border-b last:border-b-0">{children}</tr>;
}

export function SettingsTd({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn('px-2 py-1.5 align-middle', className)}>{children}</td>;
}

export function SettingsField({
  label,
  children,
}: {
  label: string;
  children: ReactElement<{ id?: string; 'aria-labelledby'?: string }>;
}) {
  const generatedId = useId();
  const controlId = children.props.id ?? `settings-field-${generatedId}`;
  const labelId = `${controlId}-label`;

  return (
    <div className="grid min-w-0 gap-1">
      <label
        id={labelId}
        htmlFor={controlId}
        className="text-2xs text-muted-foreground font-medium uppercase tracking-wide"
      >
        {label}
      </label>
      {cloneElement(children, {
        id: controlId,
        'aria-labelledby': children.props['aria-labelledby'] ?? labelId,
      })}
    </div>
  );
}

export function SettingsToggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="border-border bg-background/40 text-foreground flex h-[var(--helios-control-height,28px)] items-center justify-between gap-3 rounded-md border px-2.5 text-xs">
      <span>{label}</span>
      <input
        type="checkbox"
        className="accent-primary h-3.5 w-3.5"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export function SettingsLoadingState({ label }: { label: string }) {
  return (
    <div className="flex min-h-[160px] items-center justify-center">
      <div className="border-border bg-card text-muted-foreground flex items-center gap-2 rounded-md border px-3 py-2 text-xs">
        <Spinner className="text-primary h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </div>
    </div>
  );
}

export function SettingsMutationBanner({
  message,
  error,
}: {
  message: string | null;
  error: unknown;
}) {
  if (!message && !error) return null;
  const errorMessage = error instanceof Error ? error.message : error ? String(error) : null;
  return (
    <div
      className={
        errorMessage
          ? 'border-critical/40 bg-critical/10 text-critical rounded-md border px-3 py-1.5 text-xs'
          : 'border-success/40 bg-success/10 text-success rounded-md border px-3 py-1.5 text-xs'
      }
      role="status"
    >
      {errorMessage ?? message}
    </div>
  );
}
